import type { FC } from 'react';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/default/button';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import { CloseButton } from '@atlaskit/modal-dialog/close-button';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';

import { fg } from '@confluence/feature-gating';

import { messages } from '../../messages.ts';

type IssueCreateWarningDialogProps = {
	onConfirm: () => void;
	onCancel?: () => void;
	issuesCount?: number;
};

const headerStyles = xcss({
	flexDirection: 'row-reverse',
	width: '100%',
	justifyContent: 'space-between',
});

const footerStyles = xcss({
	display: 'flex',
	justifyContent: 'flex-end',
	flex: 1,
	gap: 'space.050',
});

export const IssueCreateWarningDialog: FC<IssueCreateWarningDialogProps> = ({
	onConfirm,
	onCancel,
	issuesCount = 1,
}) => {
	const handleConfirm = () => {
		onConfirm();
	};

	const handleCancel = () => {
		if (onCancel) {
			onCancel();
		}
	};

	const Footer = () => {
		return (
			<ModalFooter>
				<Inline xcss={footerStyles}>
					<Button testId="issue-create-warning-cancel-button" onClick={handleCancel}>
						<FormattedMessage {...messages.unsavedActionCancel} />
					</Button>
					<Button
						testId="issue-create-warning-ok-button"
						appearance="warning"
						onClick={handleConfirm}
					>
						<FormattedMessage {...messages.unsavedActionDiscard} />
					</Button>
				</Inline>
			</ModalFooter>
		);
	};

	return (
		<FormattedMessage
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			{...(fg('confluence-issue-terminology-refresh')
				? messages.unsavedHeadingIssueTermRefresh
				: messages.unsavedHeading)}
			values={{ issuesCount }}
		>
			{(text) => (
				<ModalDialog testId="modal-dialog-issue-create-warning">
					<ModalHeader>
						<Inline xcss={headerStyles}>
							<CloseButton onClick={handleCancel} />
							<ModalTitle appearance="warning">{text}</ModalTitle>
						</Inline>
					</ModalHeader>

					<ModalBody>
						<FormattedMessage
							// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
							{...(fg('confluence-issue-terminology-refresh')
								? messages.unsavedContentIssueTermRefresh
								: messages.unsavedContent)}
							values={{ issuesCount }}
						/>
					</ModalBody>
					<Footer />
				</ModalDialog>
			)}
		</FormattedMessage>
	);
};
